import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Input,
  Pagination,
  PaginationItem,
  PaginationLink,
  Spinner,
} from "reactstrap";
import BootstrapTable from "react-bootstrap-table-next";

const SEARCH_DEBOUNCE_MS = 400;

/**
 * Table shell for BACKEND-driven pagination + search.
 * The parent owns page/search state and re-fetches on change —
 * this component never slices or filters `data` itself.
 */
const DataTable = ({
  keyField,
  columns,
  data,
  page,
  totalPages,
  onPageChange,
  searchTerm,
  onSearchChange,
  loading,
  searchPlaceholder = "Search...",
}) => {
  const [searchInput, setSearchInput] = useState(searchTerm || "");

  // Stay in sync if the parent resets search from outside (e.g. after a save).
  useEffect(() => {
    setSearchInput(searchTerm || "");
  }, [searchTerm]);

  // Debounce keystrokes before triggering a backend refetch.
  useEffect(() => {
    const handle = setTimeout(() => {
      if (searchInput !== searchTerm) {
        onSearchChange(searchInput);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const pageNumbers = getPageWindow(page, totalPages);

  return (
    <div className="mt-4">
      <Row className="mb-3">
        <Col md="4">
          <Input
            type="text"
            placeholder={searchPlaceholder}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </Col>
      </Row>

      <div className="table-responsive position-relative">
        {loading && (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(255,255,255,0.6)",
              zIndex: 1,
            }}
          >
            <Spinner size="sm" color="primary" />
          </div>
        )}
        <BootstrapTable
          keyField={keyField}
          columns={columns}
          data={data}
          classes="table align-middle table-nowrap table-hover"
          bordered={false}
          striped={false}
          responsive
          noDataIndication={() => (loading ? "Loading..." : "No records found")}
        />
      </div>

      {totalPages > 1 && (
        <Row className="align-items-md-center mt-3">
          <Col className="d-flex justify-content-end">
            <Pagination className="pagination pagination-rounded mb-0">
              <PaginationItem disabled={page <= 1}>
                <PaginationLink
                  previous
                  onClick={() => onPageChange(page - 1)}
                />
              </PaginationItem>
              {pageNumbers.map((p) => (
                <PaginationItem key={p} active={p === page}>
                  <PaginationLink onClick={() => onPageChange(p)}>
                    {p}
                  </PaginationLink>
                </PaginationItem>
              ))}
              <PaginationItem disabled={page >= totalPages}>
                <PaginationLink next onClick={() => onPageChange(page + 1)} />
              </PaginationItem>
            </Pagination>
          </Col>
        </Row>
      )}
    </div>
  );
};

// Shows up to `size` page numbers, centered on the current page.
function getPageWindow(current, total, size = 5) {
  if (total <= size) return Array.from({ length: total }, (_, i) => i + 1);
  let start = Math.max(current - Math.floor(size / 2), 1);
  let end = start + size - 1;
  if (end > total) {
    end = total;
    start = end - size + 1;
  }
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

export default DataTable;
